import { Container } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import AddRecordButton from '@components/AddRecordButton';
import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import ViewTab from '@components/ViewTab';

import { ADD_FAMILY_HISTORY } from '@constants/routes';

import { useGetFamilyHistories } from '@hooks/api/family-history';
import { useSession } from '@hooks/useSession';

import { NoRecordsWrapper } from '@layouts/Layouts/BigTitleLayout';

import { capitalize } from '@utils/format';

import HistoryList from './HistoryList';
import { Spacer, WrapperDiv } from './Main.style';

const messages = defineMessages({
  title: {
    id: 'familyHistoryMain.title',
    defaultMessage: 'Family History',
  },
  maternal: {
    id: 'familyHistoryMain.maternal',
    defaultMessage: 'maternal',
  },
  paternal: {
    id: 'familyHistoryMain.paternal',
    defaultMessage: 'paternal',
  },
  nuclear: {
    id: 'familyHistoryMain.nuclear',
    defaultMessage: 'nuclear',
  },
  other: {
    id: 'GLOBAL.other',
    defaultMessage: 'other',
  },
  noRecordsFound: {
    id: 'familyHistoryMain.noRecordsFound',
    defaultMessage: 'No Known {relationship} Family History',
  },
  addARecord: {
    id: 'familyHistoryMain.addARecord',
    defaultMessage: 'Add a Record',
  },
});

const FamilyHistory: React.FC = () => {
  const { formatMessage } = useIntl();
  const {
    state: { isClient },
  } = useSession();

  const { data, loading } = useGetFamilyHistories();

  const tabs = [
    {
      id: 'maternal',
      value: formatMessage(messages.maternal),
    },
    {
      id: 'paternal',
      value: formatMessage(messages.paternal),
    },
    {
      id: 'nuclear',
      value: formatMessage(messages.nuclear),
    },
    {
      id: 'other',
      value: formatMessage(messages.other),
    },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const tabData = useMemo(
    () => data.filter((c) => c.relationship?.category === activeTab.id),
    [activeTab.id, data]
  );

  return (
    <ContentLoader loading={loading}>
      <Container maxWidth="sm">
        <WrapperDiv>
          <ViewTab
            changeTab={setActiveTab}
            selected={activeTab}
            tabArray={tabs}
          />

          <Spacer />
          <HistoryList data={tabData} />

          {tabData.length === 0 && (
            <NoRecordsWrapper>
              {formatMessage(messages.noRecordsFound, {
                relationship: capitalize(activeTab.value),
              })}
            </NoRecordsWrapper>
          )}
          {isClient && (
            <AddRecordButton link={ADD_FAMILY_HISTORY}>
              {formatMessage(messages.addARecord)}
            </AddRecordButton>
          )}
        </WrapperDiv>
      </Container>
    </ContentLoader>
  );
};

export default withErrorBoundary(FamilyHistory, {
  FallbackComponent: ErrorBoundaryFallback,
});
